import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";

import { getCookie, setCookie, deleteCookie } from "./cookies";

import { refreshToken as apiRefreshToken } from "@/services/auth";

import { useUserStore } from "@/stores/useAuthStore";

import {
  BASE_URL,
  ACCESS_TOKEN_EXPIRE_DAYS as accessTokenExpireDays,
  REFRESH_TOKEN_EXPIRE_DAYS as refreshTokenExpireDays,
} from "./auth.config";

export { BASE_URL };

type RetryRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
};

const axiosInstance: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

/* -------------------------------------------------------------------------- */
/*                                  HELPERS                                   */
/* -------------------------------------------------------------------------- */

const isBrowser = typeof window !== "undefined";

const getAccessToken = () => {
  if (!isBrowser) return null;

  return getCookie("access_token");
};

const getRefreshToken = () => {
  if (!isBrowser) return null;

  return getCookie("refresh_token");
};

const saveTokens = (accessToken: string, refreshToken?: string) => {
  deleteCookie("access_token");
  setCookie("access_token", accessToken, { days: accessTokenExpireDays });

  if (refreshToken) {
    deleteCookie("refresh_token");
    setCookie("refresh_token", refreshToken, { days: refreshTokenExpireDays });
  }
};

const clearAuth = () => {
  deleteCookie("access_token");
  deleteCookie("refresh_token");

  useUserStore.getState().clearUser();
};

const redirectToLogin = () => {
  if (!isBrowser) return;
  const currentPath = window.location.pathname;
  const publicRoutes = ["/login", "/movie/", "/s/"];
  const isPublicRoute = publicRoutes.some((route) =>
    currentPath.startsWith(route),
  );
  if (currentPath === "/account/plans") {
    window.location.replace("/login");
  }
  if (!isPublicRoute) {
    window.location.replace("/");
  }
};

/* -------------------------------------------------------------------------- */
/*                              ERROR LOGGER                                  */
/* -------------------------------------------------------------------------- */

const logAxiosError = (error: AxiosError) => {
  const status = error.response?.status;
  const code = error.code;

  if (status === 401) return;
  if (code === "ERR_CANCELED" || code === "ECONNABORTED") return;

  const url = error.config?.url;
  const method = error.config?.method?.toUpperCase();
  const where = isBrowser ? "[client]" : "[server]";

  console.warn(`${where} ${method ?? "REQ"} ${url ?? "<no-url>"} failed`, {
    status: status ?? null,
    code: code ?? null,
    message: error.message,
    data: error.response?.data,
  });
};

axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    logAxiosError(error);
    return Promise.reject(error);
  },
);

/* -------------------------------------------------------------------------- */
/*                              REQUEST INTERCEPTOR                           */
/* -------------------------------------------------------------------------- */

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();

    if (token) {
      config.headers.Authorization = token;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

/* -------------------------------------------------------------------------- */
/*                              REFRESH CONTROL                               */
/* -------------------------------------------------------------------------- */

let isRefreshing = false;

let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}[] = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });

  failedQueue = [];
};

/* -------------------------------------------------------------------------- */
/*                              RESPONSE INTERCEPTOR                          */
/* -------------------------------------------------------------------------- */

axiosInstance.interceptors.response.use(
  (response) => response,

  async (error: AxiosError) => {
    logAxiosError(error);

    const originalRequest = error.config as RetryRequestConfig;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const status = error.response?.status;

    const isUnauthorized = status === 401;

    const isAuthEndpoint =
      originalRequest.url?.includes("/auth/") ||
      originalRequest.url?.includes("/logout");

    if (!isUnauthorized || originalRequest._retry || isAuthEndpoint) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      clearAuth();
      redirectToLogin();

      return Promise.reject(error);
    }

    /* ---------------------------------------------------------------------- */
    /*                             REFRESH IN PROGRESS                        */
    /* ---------------------------------------------------------------------- */

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string) => {
            if (!originalRequest.headers) {
              originalRequest.headers = {};
            }

            originalRequest.headers.Authorization = token;

            resolve(axiosInstance(originalRequest));
          },

          reject: (err) => {
            reject(err);
          },
        });
      });
    }

    /* ---------------------------------------------------------------------- */
    /*                               START REFRESH                            */
    /* ---------------------------------------------------------------------- */

    isRefreshing = true;

    try {
      const response = await apiRefreshToken({
        refresh_token: refreshToken,
      });

      const newAccessToken = response?.data?.tokens?.access_token;

      const newRefreshToken = response?.data?.tokens?.refresh_token;

      const userProfile = response?.data?.user_profile;
      if (!newAccessToken) {
        clearAuth();

        redirectToLogin();

        return Promise.reject();
      }
      saveTokens(newAccessToken, newRefreshToken);

      if (userProfile) {
        useUserStore.getState().updateUserProfile(userProfile);
      }

      processQueue(null, newAccessToken);

      if (!originalRequest.headers) {
        originalRequest.headers = {};
      }

      originalRequest.headers.Authorization = newAccessToken;

      return axiosInstance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);

      clearAuth();

      redirectToLogin();

      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default axiosInstance;
